import { ArrowUpRight, Phone, MapPin, Clock } from "lucide-react";
import { useBooking } from "@/components/site/BookingProvider";

// Maui starry mountain landscape — sense of place
const HERO_IMG =
  "https://stcdn.leadconnectorhq.com/funnel/photo-video-gallery/jonatan-pie-VeiHRfGxTzY-unsplash.jpg";

export default function Hero() {
  const { openBooking } = useBooking();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full flex items-end overflow-hidden"
    >
      {/* Image */}
      <div
        className="mll-hero-image absolute inset-0"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
        aria-hidden="true"
      />
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-stone/55 via-stone/40 to-stone/90"
        aria-hidden="true"
      />
      <div className="absolute inset-0 mll-grain opacity-90" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 pt-32 pb-16 sm:pt-40 sm:pb-24">
        <div className="mll-stagger max-w-4xl text-bone">
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.32em] font-semibold text-bone/80">
            <span className="inline-block w-8 h-px bg-bone/60 align-middle mr-3" />
            Haiku · Maui · Hawai‘i
          </p>

          <h1
            data-testid="hero-headline"
            className="font-serif font-medium tracking-tight text-5xl sm:text-7xl lg:text-[88px] leading-[1.02] mt-6"
          >
            The formula for{" "}
            <em className="italic font-normal text-clay-soft">longevity</em>,
            in the heart of Haiku Maui.
          </h1>

          <p className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-bone/85 font-light">
            A state-of-the-art biohacking lounge in Haiku, where clinical-grade
            PEMF, red light, and hyperbaric oxygen therapy meet the quiet
            rhythm of Maui. Heal on a cellular level — by appointment.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={openBooking}
              data-testid="hero-book-button"
              className="group inline-flex items-center gap-3 bg-bone text-stone hover:bg-clay-soft hover:text-bone px-7 h-14 text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors"
            >
              Book a Session
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              href="tel:8085008857"
              data-testid="hero-phone-button"
              className="inline-flex items-center gap-3 border border-bone/40 text-bone hover:bg-bone/10 px-7 h-14 text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors"
            >
              <Phone className="w-4 h-4" /> 808.500.8857
            </a>
          </div>
        </div>

        {/* Bottom info strip */}
        <div className="mll-reveal mt-20 sm:mt-28 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 text-bone/85 border-t border-bone/15 pt-8">
          <Info
            icon={<MapPin className="w-4 h-4" />}
            label="Location"
            value="810 Kokomo Rd, Suite 120 · Haiku, HI 96708"
          />
          <Info
            icon={<Clock className="w-4 h-4" />}
            label="Hours"
            value="Monday — Friday · 8am – 5pm"
          />
          <Info
            icon={<Phone className="w-4 h-4" />}
            label="Direct line"
            value="808.500.8857 (call or text)"
          />
        </div>
      </div>
    </section>
  );
}

function Info({ icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <span className="mt-1 inline-flex items-center justify-center w-8 h-8 rounded-full border border-bone/30">
        {icon}
      </span>
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-bone/55">
          {label}
        </p>
        <p className="text-sm sm:text-base font-light mt-1 leading-snug">
          {value}
        </p>
      </div>
    </div>
  );
}
